import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Social link slot groups ───────────────────────────────────────────────────
// Each social link is a url slot group bound to the matching property field so
// the icons appear automatically when a property has that channel filled in.
// `_target` stays a hidden field to keep the group compliant with link rules.

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const googleLinkDef: SlotGroupDefinition = {
  label: 'Google Business',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Google' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ─── Image slot groups ──────────────────────────────────────────────────────────
// Four square photos, each bound to a photo_gallery item so the editor can swap
// the whole tile to another gallery photo in a single action.

const image1Def: SlotGroupDefinition = {
  label: 'Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image2Def: SlotGroupDefinition = {
  label: 'Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image3Def: SlotGroupDefinition = {
  label: 'Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image4Def: SlotGroupDefinition = {
  label: 'Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

// Mobile ~half viewport; tablet tiles share the row; desktop keeps compact squares.
const IMAGE_SIZES =
  '(min-width: 1024px) 12vw, (min-width: 768px) 18vw, (min-width: 640px) 22vw, 50vw';
const iconLinkClasses =
  'flex h-6 w-6 items-center justify-center text-[var(--vibe-color-text)] transition-colors duration-300 hover:text-[var(--vibe-color-primary)]';
// Fixed tile sizes only on large-phone (sm) and desktop (lg+). Tablet (md–lg)
// lets each cell fill the growing 4-col grid so the strip matches the reference.
const photoTileClasses =
  'aspect-square w-full min-w-0 overflow-hidden sm:w-28 md:w-full lg:w-36';

export function SocialConnect() {
  const title = useContentToken('socialconnect.title');

  const facebook = useSlotGroup('socialconnect.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('socialconnect.instagram_link', instagramLinkDef);
  const google = useSlotGroup('socialconnect.google_link', googleLinkDef);

  const image1 = useSlotGroup('socialconnect.image1', image1Def);
  const image2 = useSlotGroup('socialconnect.image2', image2Def);
  const image3 = useSlotGroup('socialconnect.image3', image3Def);
  const image4 = useSlotGroup('socialconnect.image4', image4Def);

  return (
    <section className="w-full bg-white pt-12 md:pt-14 md:px-5 lg:pt-16 lg:px-0 pb-6 md:pb-8">
      {/*
        Mobile: stacked — centered copy over a 2×2 photo grid.
        Tablet only (md–lg): side-by-side — title/icons left, four squares with a
        thin white grid gutter filling the remaining width.
        Desktop (lg+): centered composition with a compact fixed-size photo row.
      */}
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-10 md:flex-row md:items-center md:justify-between md:gap-10 lg:justify-center lg:items-start lg:gap-16">

        {/* ── Title + social links ── */}
        <div className="flex shrink-0 flex-col items-center px-4 text-center sm:px-6 md:px-0 lg:px-8">
          <EditableToken
            path="socialconnect.title"
            as="h2"
            className="sc-section-title font-heading text-2xl sm:text-3xl md:text-[2rem] lg:text-4xl leading-tight text-[var(--vibe-color-text)]"
          >
            {title}
          </EditableToken>

          <div className="mt-6 flex items-center justify-center gap-5">
            {facebook.url && (
              <EditableSlotGroup groupPath="socialconnect.facebook_link" definition={facebookLinkDef}>
                <a
                  href={facebook.url}
                  target={facebook._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label={facebook.label || 'Facebook'}
                  className={iconLinkClasses}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-full w-full" aria-hidden="true">
                    <path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4v-8.5z" />
                  </svg>
                </a>
              </EditableSlotGroup>
            )}

            {instagram.url && (
              <EditableSlotGroup groupPath="socialconnect.instagram_link" definition={instagramLinkDef}>
                <a
                  href={instagram.url}
                  target={instagram._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label={instagram.label || 'Instagram'}
                  className={iconLinkClasses}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-full w-full" aria-hidden="true">
                    <path d="M12 2.2c3.2 0 3.6 0 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s0-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2zm0 1.8c-3.15 0-3.5 0-4.74.07-.89.04-1.37.19-1.69.31-.43.17-.73.36-1.05.68-.32.32-.51.62-.68 1.05-.12.32-.27.8-.31 1.69C3.26 8.5 3.25 8.85 3.25 12s0 3.5.07 4.74c.04.89.19 1.37.31 1.69.17.43.36.73.68 1.05.32.32.62.51 1.05.68.32.12.8.27 1.69.31 1.24.07 1.59.07 4.74.07s3.5 0 4.74-.07c.89-.04 1.37-.19 1.69-.31.43-.17.73-.36 1.05-.68.32-.32.51-.62.68-1.05.12-.32.27-.8.31-1.69.07-1.24.07-1.59.07-4.74s0-3.5-.07-4.74c-.04-.89-.19-1.37-.31-1.69a2.8 2.8 0 0 0-.68-1.05 2.8 2.8 0 0 0-1.05-.68c-.32-.12-.8-.27-1.69-.31C15.5 4 15.15 4 12 4zm0 3.06a4.94 4.94 0 1 1 0 9.88 4.94 4.94 0 0 1 0-9.88zm0 1.8a3.14 3.14 0 1 0 0 6.28 3.14 3.14 0 0 0 0-6.28zm5.15-3.24a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3z" />
                  </svg>
                </a>
              </EditableSlotGroup>
            )}

            {google.url && (
              <EditableSlotGroup groupPath="socialconnect.google_link" definition={googleLinkDef}>
                <a
                  href={google.url}
                  target={google._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label={google.label || 'Google'}
                  className={iconLinkClasses}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-full w-full" aria-hidden="true">
                    <path d="M12 11v2.9h4.05c-.17 1.06-.68 1.96-1.45 2.57v2.13h2.35C18.7 17.33 19.5 14.87 19.5 12c0-.66-.06-1.29-.17-1.9H12V11z" />
                    <path d="M12 20c1.96 0 3.6-.65 4.8-1.76l-2.35-2.13c-.65.44-1.49.7-2.45.7-1.88 0-3.48-1.27-4.05-2.98H5.52v2.2A8 8 0 0 0 12 20z" />
                    <path d="M7.95 13.83A4.8 4.8 0 0 1 7.7 12c0-.64.11-1.26.25-1.83v-2.2H5.52A8 8 0 0 0 4 12c0 1.29.31 2.51.85 3.6l2.29-1.77z" />
                    <path d="M12 7.19c1.06 0 2.02.37 2.77 1.08l2.08-2.08C15.6 5.03 13.96 4 12 4a8 8 0 0 0-6.48 3.4l2.43 2.2C8.52 8.47 10.12 7.19 12 7.19z" />
                  </svg>
                </a>
              </EditableSlotGroup>
            )}
          </div>
        </div>

        {/* ── Four square photos ──
            Mobile: edge-to-edge 2×2.
            Tablet (md–lg): 4 squares with a thin white gap, growing into the remaining row width.
            Desktop (lg+): compact fixed-size row, nearly flush. */}
        <div className="grid w-full grid-cols-2 gap-0 px-4 sm:w-auto sm:grid-cols-4 sm:gap-1 sm:px-0 md:min-w-0 md:w-full md:flex-1 md:gap-1 lg:w-auto lg:flex-none lg:gap-1">
          <div className={photoTileClasses}>
            <EditableSlotGroup groupPath="socialconnect.image1" definition={image1Def} className="block h-full w-full">
              {image1.image ? (
                <VibesitesImage
                  url={image1.image}
                  alt={image1.alt || ''}
                  sizes={IMAGE_SIZES}
                  className="h-full w-full object-cover object-center"
                />
              ) : (
                <div className="h-full w-full bg-[var(--vibe-color-surface)]" />
              )}
            </EditableSlotGroup>
          </div>

          <div className={photoTileClasses}>
            <EditableSlotGroup groupPath="socialconnect.image2" definition={image2Def} className="block h-full w-full">
              {image2.image ? (
                <VibesitesImage
                  url={image2.image}
                  alt={image2.alt || ''}
                  sizes={IMAGE_SIZES}
                  className="h-full w-full object-cover object-center"
                />
              ) : (
                <div className="h-full w-full bg-[var(--vibe-color-surface)]" />
              )}
            </EditableSlotGroup>
          </div>

          <div className={photoTileClasses}>
            <EditableSlotGroup groupPath="socialconnect.image3" definition={image3Def} className="block h-full w-full">
              {image3.image ? (
                <VibesitesImage
                  url={image3.image}
                  alt={image3.alt || ''}
                  sizes={IMAGE_SIZES}
                  className="h-full w-full object-cover object-center"
                />
              ) : (
                <div className="h-full w-full bg-[var(--vibe-color-surface)]" />
              )}
            </EditableSlotGroup>
          </div>

          <div className={photoTileClasses}>
            <EditableSlotGroup groupPath="socialconnect.image4" definition={image4Def} className="block h-full w-full">
              {image4.image ? (
                <VibesitesImage
                  url={image4.image}
                  alt={image4.alt || ''}
                  sizes={IMAGE_SIZES}
                  className="h-full w-full object-cover object-center"
                />
              ) : (
                <div className="h-full w-full bg-[var(--vibe-color-surface)]" />
              )}
            </EditableSlotGroup>
          </div>
        </div>

      </div>

      <style>{`
        /* Match the FloorplansList section heading: font-heading (applied via
           the element's class) + text color. */
        .sc-section-title {
          color: var(--vibe-color-text);
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'SocialConnect',
  displayName: 'Social Connect',
  tokens: {
    'socialconnect.title': {
      label: 'Title',
      type: 'text',
      defaultValue: "Let's Be Friends",
    },
  },
  slotGroups: {
    'socialconnect.facebook_link': facebookLinkDef,
    'socialconnect.instagram_link': instagramLinkDef,
    'socialconnect.google_link': googleLinkDef,
    'socialconnect.image1': image1Def,
    'socialconnect.image2': image2Def,
    'socialconnect.image3': image3Def,
    'socialconnect.image4': image4Def,
  },
};

(SocialConnect as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SocialConnect;
